import { Box } from '@mui/material';
import { useId } from 'react';
import { AnimatedNumberText } from '@/components/AnimatedNumber';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SYSTEM WYKRESÓW — prymitywy przeniesione z makiety (warstwa wspólna nr 3)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * DLACZEGO WŁASNE SVG, A NIE BIBLIOTEKA. Nie z upodobania do pisania od zera. Biblioteka wykresów
 * przynosi własny język wizualny — swoją siatkę, swoje osie, swoją typografię i swoją animację —
 * i albo go akceptujesz, albo walczysz z nim nadpisaniami. Makieta ma tu bardzo konkretne decyzje
 * (siatka kreskowana bez linii zerowej, seria porównawcza cieńsza i szara, brak wykresu kołowego),
 * a odwzorowanie ich w cudzym silniku kosztuje więcej niż napisanie kilkuset linii SVG.
 * Do tego dochodzi waga: najlżejsze biblioteki zaczynają się od kilkudziesięciu kilobajtów.
 *
 * KONTRAKT DOSTĘPNOŚCI — obowiązuje KAŻDY wykres w tym pliku:
 *   • `role="img"` i `<title>` z pełnym opisem słownym — kolor nigdy nie jest jedynym nośnikiem;
 *   • podpis, podstawa metryki, okno i klasyfikacja źródła stoją PRZY wykresie, nie w przypisie;
 *   • stan pusty jest osobnym komponentem z własnym komunikatem, a nie wykresem bez danych.
 *
 * PRAWDA DANYCH WYGRYWA ZE STYLEM. Gdy serii nie ma, rysujemy RAMĘ i uczciwy komunikat —
 * nigdy linii przy zerze. Linia na zerze mówi „zmierzyliśmy i wyszło zero"; brak danych znaczy
 * coś zupełnie innego i pomylenie tych dwóch rzeczy jest w tym produkcie najdroższym błędem.
 */

const SZEROKOSC = 720;

/** Podziałka osi: 1/2/5 × potęga dziesiątki. Bez tego etykiety wychodzą w rodzaju „3 471,29". */
function podzialka(max: number, ile = 4): number[] {
  if (max <= 0) return [0];
  const surowy = max / ile;
  const rzad = Math.pow(10, Math.floor(Math.log10(surowy)));
  const z = surowy / rzad;
  const krok = (z <= 1 ? 1 : z <= 2 ? 2 : z <= 5 ? 5 : 10) * rzad;
  const wynik: number[] = [];
  for (let v = 0; v <= max + krok * 0.001; v += krok) wynik.push(v);
  return wynik;
}

export interface PunktSerii {
  etykieta: string;
  wartosc: number;
}

/* ─── STAN PUSTY ─────────────────────────────────────────────────────────────────────────────
   Osobny komponent, nie wariant wykresu. To jest miejsce, w którym produkt najczęściej kłamie:
   pusty wykres z osią i siatką wygląda jak pomiar, który wypadł nisko. */
export function PustyWykres({
  wysokosc = 220,
  komunikat,
  tytul = 'Za mało danych, żeby narysować',
}: {
  wysokosc?: number;
  komunikat: string;
  tytul?: string;
}) {
  return (
    <Box className="stan za-malo-danych" sx={{ minHeight: wysokosc }}>
      <Box className="stan-ikona" aria-hidden>
        <svg viewBox="0 0 24 24">
          <path d="M3 3v18h18" />
          <path d="M7 15l3-4 3 3 5-7" />
        </svg>
      </Box>
      <Box className="stan-tytul">{tytul}</Box>
      <Box className="stan-opis">{komunikat}</Box>
    </Box>
  );
}

/* ─── SZEREG CZASOWY ─────────────────────────────────────────────────────────────────────────
   Podstawowy wykres produktu. Rysowany od lewej, bo tak czyta się czas. Seria porównawcza leży
   POD główną, szara i przerywana — nigdy nie rywalizuje o uwagę.

   ZERA W ŚRODKU SERII SĄ RYSOWANE JAKO ZERA, nie pomijane. Pominięcie zrobiłoby z piątku
   i poniedziałku sąsiadów i skłamało o tempie zmiany. */
export function SzeregCzasowy({
  seria,
  porownanie = null,
  wysokosc = 260,
  format,
  etykietaSerii = 'Bieżący okres',
  etykietaPorownania = 'Poprzedni okres',
  kolor = 'var(--seria-1)',
  wypelnienie = true,
  komunikatPusty = 'Szereg czasowy wymaga co najmniej dwóch punktów pomiaru.',
}: {
  seria: readonly PunktSerii[] | null | undefined;
  porownanie?: readonly PunktSerii[] | null;
  wysokosc?: number;
  format: (v: number, o?: { skrot?: boolean }) => string;
  etykietaSerii?: string;
  etykietaPorownania?: string;
  kolor?: string;
  wypelnienie?: boolean;
  komunikatPusty?: string;
}) {
  const gradId = useId().replace(/:/g, '');

  if (!seria || seria.length < 2) return <PustyWykres wysokosc={wysokosc} komunikat={komunikatPusty} />;

  const marginesL = 62;
  const marginesD = 26;
  const marginesG = 14;
  const w = SZEROKOSC - marginesL - 8;
  const h = wysokosc - marginesD - marginesG;

  const maxWartosc = Math.max(...seria.map((p) => p.wartosc), ...(porownanie ? porownanie.map((p) => p.wartosc) : [0]));
  const osie = podzialka(maxWartosc);
  const gora = osie[osie.length - 1] || 1;

  const x = (i: number) => marginesL + (i / (seria.length - 1)) * w;
  const y = (v: number) => marginesG + h - (v / gora) * h;
  const sciezka = (dane: readonly PunktSerii[]) =>
    dane.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.wartosc).toFixed(1)}`).join(' ');

  const liniaGlowna = sciezka(seria);
  const obszar = `${liniaGlowna} L${x(seria.length - 1).toFixed(1)},${marginesG + h} L${marginesL},${marginesG + h} Z`;
  const krok = Math.ceil(seria.length / 8);
  const opis =
    `${etykietaSerii}: od ${format(seria[0]!.wartosc)} do ${format(seria[seria.length - 1]!.wartosc)}, ` +
    `maksimum ${format(maxWartosc)}, ${seria.length} punktów pomiaru.`;

  return (
    <>
      <svg className="wykres" viewBox={`0 0 ${SZEROKOSC} ${wysokosc}`} role="img" style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
        <title>{opis}</title>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={kolor} stopOpacity="0.28" />
            <stop offset="100%" stopColor={kolor} stopOpacity="0" />
          </linearGradient>
        </defs>

        {osie.map((v) => (
          <g key={v}>
            <line
              x1={marginesL}
              y1={y(v).toFixed(1)}
              x2={SZEROKOSC - 8}
              y2={y(v).toFixed(1)}
              stroke="var(--linia)"
              strokeWidth="1"
              strokeDasharray={v === 0 ? undefined : '3 5'}
            />
            <text x={marginesL - 10} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="11" fill="var(--tekst-slaby)">
              {format(v, { skrot: true })}
            </text>
          </g>
        ))}

        {porownanie ? (
          <path
            d={sciezka(porownanie)}
            fill="none"
            stroke="var(--seria-porownawcza)"
            strokeWidth="2"
            strokeDasharray="4 4"
            strokeLinecap="round"
          />
        ) : null}

        {wypelnienie ? <path d={obszar} fill={`url(#${gradId})`} className="sygnal-obszar" /> : null}

        <path
          d={liniaGlowna}
          fill="none"
          stroke={kolor}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="sygnal-linia"
          style={{ ['--dlugosc' as string]: 2600 }}
        />

        {seria.map((p, i) =>
          i % krok === 0 || i === seria.length - 1 ? (
            <text key={p.etykieta} x={x(i).toFixed(1)} y={wysokosc - 6} textAnchor="middle" fontSize="11" fill="var(--tekst-slaby)">
              {p.etykieta}
            </text>
          ) : null,
        )}
      </svg>

      <Box className="legenda">
        <span>
          <i style={{ background: kolor }} />
          {etykietaSerii}
        </span>
        {porownanie ? (
          <span>
            <i className="przerywana" />
            {etykietaPorownania}
          </span>
        ) : null}
      </Box>
    </>
  );
}

export interface PozycjaRankingu {
  nazwa: string;
  wartosc: number;
  /** Adres docelowy. Gdy jest — wiersz staje się odnośnikiem, a nie klikalnym `div`-em. */
  klucz?: string;
  /** Zmiana procentowa. `null` znaczy „nie da się policzyć", nie „zero". */
  zmiana?: number | null;
}

/* ─── SŁUPKI RANKINGOWE ──────────────────────────────────────────────────────────────────────
   Poziome, bo etykiety są długie (nazwy liceów). Pionowe wymuszałyby obracanie tekstu. */
export function SlupkiRankingowe({
  pozycje,
  format,
  kolor = 'var(--seria-1)',
  maks = 8,
  komunikatPusty = 'Brak pozycji do uszeregowania w wybranym oknie.',
  numeracja = true,
}: {
  pozycje: readonly PozycjaRankingu[] | null | undefined;
  format: (v: number, o?: { skrot?: boolean }) => string;
  kolor?: string;
  maks?: number;
  komunikatPusty?: string;
  /**
   * Numer miejsca przed nazwą. `false` dla zbiorów, w których kolejność jest NATURALNA,
   * a nie wynikiem uszeregowania — rytm tygodnia od poniedziałku do niedzieli, godziny doby.
   * Numer w takim zbiorze twierdziłby, że poniedziałek „wygrał", a on tylko stoi pierwszy.
   */
  numeracja?: boolean;
}) {
  if (!pozycje || pozycje.length === 0) return <PustyWykres wysokosc={200} komunikat={komunikatPusty} />;

  const widoczne = pozycje.slice(0, maks);
  const max = Math.max(...widoczne.map((p) => p.wartosc)) || 1;

  return (
    <Box className="ranking" role="list">
      {widoczne.map((p, i) => {
        const tresc = (
          <>
            {numeracja ? <span className="ranking-miejsce">{i + 1}</span> : null}
            <span className="ranking-nazwa" title={p.nazwa}>
              {p.nazwa}
            </span>
            <span className="ranking-tor">
              <span
                className="ranking-pasek"
                style={{ width: `${((p.wartosc / max) * 100).toFixed(1)}%`, background: kolor, animationDelay: `${i * 45}ms` }}
              />
            </span>
            <span className="ranking-wartosc">{format(p.wartosc, { skrot: true })}</span>
            <span className={`ranking-zmiana ${p.zmiana == null ? '' : p.zmiana > 1 ? 'w-gore' : p.zmiana < -1 ? 'w-dol' : 'plasko'}`}>
              {p.zmiana == null ? null : (
                <>
                  <Strzalka v={p.zmiana} />
                  {`${p.zmiana > 0 ? '+' : ''}${p.zmiana.toFixed(1)}%`}
                </>
              )}
            </span>
          </>
        );
        /* Wiersz klikalny MUSI być odnośnikiem. `div` z obsługą kliknięcia działa myszą, jest
           niedostępny z klawiatury, a czytnik ekranu ogłasza „element listy" — czyli kłamie
           o tym, co można z nim zrobić. */
        return p.klucz ? (
          <a key={p.nazwa} className="ranking-wiersz" role="listitem" href={p.klucz}>
            {tresc}
          </a>
        ) : (
          <div key={p.nazwa} className="ranking-wiersz" role="listitem">
            {tresc}
          </div>
        );
      })}
    </Box>
  );
}

function Strzalka({ v }: { v: number }) {
  /* Kierunek NIGDY nie jest niesiony samym kolorem — zawsze towarzyszy mu strzałka i znak.
     Osiem procent mężczyzn nie odróżnia czerwieni od zieleni; kolor jako jedyny nośnik to nie
     „drobiazg dostępności", tylko liczba przeczytana na odwrót. */
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      {v > 1 ? (
        <>
          <path d="M12 19V5" />
          <path d="M5 12l7-7 7 7" />
        </>
      ) : v < -1 ? (
        <>
          <path d="M12 5v14" />
          <path d="M19 12l-7 7-7-7" />
        </>
      ) : (
        <path d="M5 12h14" />
      )}
    </svg>
  );
}

/* ─── UDZIAŁ ─────────────────────────────────────────────────────────────────────────────────
   Świadomie NIE kołowy. Wycinki koła są nieporównywalne wzrokiem; ten sam zbiór na pasku czyta
   się dokładnie, a przy tym mieści się w wierszu. */
export function Udzial({
  czesci,
  format,
  wysokosc = 22,
  komunikatPusty = 'Suma udziałów wynosi zero — nie ma czego dzielić.',
}: {
  czesci: readonly { nazwa: string; wartosc: number }[] | null | undefined;
  format: (v: number) => string;
  wysokosc?: number;
  komunikatPusty?: string;
}) {
  const razem = (czesci ?? []).reduce((a, c) => a + c.wartosc, 0);
  if (!czesci || razem <= 0) return <PustyWykres wysokosc={120} komunikat={komunikatPusty} />;

  const opis = czesci.map((c) => `${c.nazwa}: ${((c.wartosc / razem) * 100).toFixed(0)}% (${format(c.wartosc)})`).join(', ');

  return (
    <Box className="udzial">
      <Box className="udzial-pasek" sx={{ height: wysokosc }} role="img" aria-label={`Udział w całości, razem ${format(razem)}. ${opis}`}>
        {czesci.map((c, i) => (
          <span
            key={c.nazwa}
            style={{
              width: `${((c.wartosc / razem) * 100).toFixed(2)}%`,
              background: `var(--seria-${(i % 7) + 1})`,
              animationDelay: `${i * 60}ms`,
            }}
          />
        ))}
      </Box>
      <Box className="legenda">
        {czesci.map((c, i) => (
          <span key={c.nazwa}>
            <i style={{ background: `var(--seria-${(i % 7) + 1})` }} />
            {c.nazwa} <b>{((c.wartosc / razem) * 100).toFixed(0)}%</b>
          </span>
        ))}
      </Box>
    </Box>
  );
}

/* ─── PIERŚCIEŃ ──────────────────────────────────────────────────────────────────────────────
   Jedna wartość procentowa jako grafika. Świadomie NIE wykres kołowy z wycinkami: pierścień
   pokazuje JEDNĄ wielkość wobec całości i nie kusi do porównywania kilku naraz, czego oko i tak
   nie robi dobrze.

   `null` rysuje pusty pierścień z kreską zamiast liczby. Zero i „nie wiemy" muszą wyglądać
   inaczej — pierścień w 0% mówi „zmierzyliśmy i wyszło zero", co przy braku danych jest kłamstwem. */
export function Pierscien({
  wartosc,
  rozmiar = 132,
  kolor = 'var(--zloto)',
  etykieta = '',
  naCiemnym = false,
  kartaId,
}: {
  wartosc: number | null;
  rozmiar?: number;
  kolor?: string;
  etykieta?: string;
  /**
   * Wariant dla ciemnej płyty-bohatera. Bez niego liczba w środku dziedziczy `--tekst`
   * (atrament) i na ciemnym tle jest praktycznie niewidoczna — a to JEST ta liczba,
   * dla której cały pierścień istnieje.
   */
  naCiemnym?: boolean;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15) — ta sama konwencja co w
   * `PlytaGlowna`, `MiniMetryka`, `MetricCard` i `Karta`.
   *
   * Komponent jest WSPÓŁDZIELONY i renderuje się w dwóch rolach: jako pierścień WEWNĄTRZ
   * `PlytaGlowna` (wtedy adres niesie sama płyta i tu nie ma go po co powtarzać) oraz jako
   * samodzielna karta — na ekranie „Dziś" jest to pierścień punktualności w karcie
   * „Dostawy dziś" (HOME-13). Identyfikator musi więc pochodzić z miejsca użycia.
   *
   * Korzeniem jest `<svg>`; atrybut `data-*` przechodzi na element SVG tak samo jak na HTML
   * i nie zmienia ani wyglądu, ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  const r = rozmiar / 2 - 10;
  const obwod = 2 * Math.PI * r;
  const znane = wartosc !== null && Number.isFinite(wartosc);
  const wypelnione = znane ? (Math.max(0, Math.min(100, wartosc)) / 100) * obwod : 0;
  const opis = znane ? `${etykieta}: ${wartosc}%` : `${etykieta}: brak danych`;

  return (
    <svg
      viewBox={`0 0 ${rozmiar} ${rozmiar}`}
      width={rozmiar}
      height={rozmiar}
      role="img"
      aria-label={opis}
      data-cc-card-id={kartaId}
    >
      <title>{opis}</title>
      <circle
        cx={rozmiar / 2}
        cy={rozmiar / 2}
        r={r}
        fill="none"
        stroke={naCiemnym ? 'rgba(255,255,255,0.16)' : 'var(--linia)'}
        strokeWidth="9"
      />
      {znane ? (
        <circle
          cx={rozmiar / 2}
          cy={rozmiar / 2}
          r={r}
          fill="none"
          stroke={kolor}
          strokeWidth="9"
          strokeLinecap="round"
          transform={`rotate(-90 ${rozmiar / 2} ${rozmiar / 2})`}
          strokeDasharray={`${wypelnione.toFixed(1)} ${obwod.toFixed(1)}`}
        />
      ) : null}
      <text
        x={rozmiar / 2}
        y={rozmiar / 2 + 3}
        textAnchor="middle"
        fontFamily="var(--krój-display)"
        fontSize={znane ? 30 : 18}
        fill={
          naCiemnym
            ? znane
              ? '#fff'
              : 'rgba(255,255,255,0.6)'
            : znane
              ? 'var(--tekst)'
              : 'var(--tekst-slaby)'
        }
      >
        {/* Count-up w <tspan>: rysunek ma własną etykietę dostępności z wartością końcową (aria-label). */}
        {znane ? <AnimatedNumberText text={String(wartosc)} wariant="svg" /> : '—'}
        {znane ? <tspan fontSize="15">%</tspan> : null}
      </text>
      {etykieta ? (
        <text
          x={rozmiar / 2}
          y={rozmiar / 2 + 24}
          textAnchor="middle"
          fontSize="10.5"
          fill={naCiemnym ? 'rgba(255,255,255,0.6)' : 'var(--tekst-slaby)'}
        >
          {etykieta}
        </text>
      ) : null}
    </svg>
  );
}
